import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSearch, FiFilter, FiRefreshCw, FiEdit2, FiX, FiSave, FiEye, FiPlus, FiTrash2, FiDownload } from 'react-icons/fi';
import { FaUserTie } from 'react-icons/fa';
import './Staff.css';

const Staff = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState(''); // 'manual' or 'excel'
  const [excelFile, setExcelFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [newStaffForm, setNewStaffForm] = useState({
    name: '',
    nip: '',
    email: '',
    phone: '',
    gender: '',
    birthPlace: '',
    birthDate: '',
    address: '',
    division: '',
    position: '',
    status: 'Aktif',
    isActive: true
  });
  const [syncConfig, setSyncConfig] = useState({
    limit: 50,
    syncMode: 'upsert'
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    byDivision: []
  });

  useEffect(() => {
    fetchStaff();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, divisionFilter]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (divisionFilter) params.division = divisionFilter;

      const res = await axios.get('/api/staff', { params });
      setStaffList(res.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal mengambil data staff');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/staff/stats');
      setStats(res.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDivisionFilter('');
    fetchStaff();
    fetchStats();
  };

  const handleSyncStaff = async () => {
    setSyncing(true);
    try {
      const res = await axios.post('/api/neofeeder/sync/staff', syncConfig);
      toast.success(res.data.message || 'Sinkronisasi staff berhasil!');
      setShowSyncModal(false);
      fetchStaff();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sinkronisasi staff gagal');
      console.error('Sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleViewDetail = (staff) => {
    setSelectedStaff(staff);
    setIsEditMode(false);
    setEditForm({
      name: staff.name || '',
      nip: staff.nip || '',
      email: staff.email || '',
      phone: staff.phone || '',
      gender: staff.gender || '',
      birthPlace: staff.birthPlace || '',
      birthDate: staff.birthDate ? new Date(staff.birthDate).toISOString().split('T')[0] : '',
      address: staff.address || '',
      division: staff.division || '',
      position: staff.position || '',
      status: staff.status || 'Aktif',
      isActive: staff.isActive !== undefined ? staff.isActive : true
    });
    
    // Set photo preview
    if (staff.photo && staff.photo !== 'default-avatar.png') {
      setPhotoPreview(`/uploads/staff/${staff.photo}`);
    } else {
      setPhotoPreview('/default-avatar.png');
    }
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStaff(null);
    setIsEditMode(false);
    setEditForm({});
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handleEditMode = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Reset form to original data
    if (selectedStaff) {
      setEditForm({
        name: selectedStaff.name || '',
        nip: selectedStaff.nip || '',
        email: selectedStaff.email || '',
        phone: selectedStaff.phone || '',
        gender: selectedStaff.gender || '',
        birthPlace: selectedStaff.birthPlace || '',
        birthDate: selectedStaff.birthDate ? new Date(selectedStaff.birthDate).toISOString().split('T')[0] : '',
        address: selectedStaff.address || '',
        division: selectedStaff.division || '',
        position: selectedStaff.position || '',
        status: selectedStaff.status || 'Aktif',
        isActive: selectedStaff.isActive !== undefined ? selectedStaff.isActive : true
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedStaff) return;
    
    try {
      await axios.put(`/api/staff/${selectedStaff._id}`, editForm);
      
      // Upload photo if selected
      if (photoFile) {
        const photoFormData = new FormData();
        photoFormData.append('photo', photoFile);
        
        await axios.post(`/api/staff/${selectedStaff._id}/upload-photo`, photoFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      toast.success('Data staff berhasil diperbarui');
      
      // Update selected staff with new data
      setSelectedStaff({ ...selectedStaff, ...editForm });
      setIsEditMode(false);
      
      // Refresh list
      fetchStaff();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal memperbarui data staff');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('File harus berupa gambar');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 5MB');
        return;
      }

      setPhotoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(selectedStaff?.photo && selectedStaff.photo !== 'default-avatar.png' 
      ? `/uploads/staff/${selectedStaff.photo}` 
      : '/default-avatar.png');
    
    // Reset file input
    const fileInput = document.getElementById('staff-photo-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNewStaffInputChange = (field, value) => {
    setNewStaffForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddStaff = async () => {
    try {
      await axios.post('/api/staff', newStaffForm);
      toast.success('Staff berhasil ditambahkan');
      setShowAddModal(false);
      setAddMode('');
      setNewStaffForm({
        name: '',
        nip: '',
        email: '',
        phone: '',
        gender: '',
        birthPlace: '',
        birthDate: '',
        address: '',
        division: '',
        position: '',
        status: 'Aktif',
        isActive: true
      });
      fetchStaff();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menambahkan staff');
    }
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      toast.error('Pilih file Excel terlebih dahulu');
      return;
    }

    const formData = new FormData();
    formData.append('file', excelFile);

    try {
      const res = await axios.post('/api/staff/import-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(res.data.message || 'Import Excel berhasil!');
      setShowAddModal(false);
      setAddMode('');
      setExcelFile(null);
      fetchStaff();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Import Excel gagal');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      if (!validTypes.includes(file.type)) {
        toast.error('File harus berupa Excel (.xls atau .xlsx)');
        return;
      }
      setExcelFile(file);
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus staff ini?')) {
      try {
        await axios.delete(`/api/staff/${staffId}`);
        toast.success('Staff berhasil dihapus');
        fetchStaff();
        fetchStats();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Gagal menghapus staff');
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusBadge = (isActive, status) => {
    if (isActive) {
      return <span className="table-badge table-badge-success">Aktif</span>;
    } else {
      return <span className="table-badge table-badge-secondary">{status || 'Tidak Aktif'}</span>;
    }
  };

  // Get unique divisions for filter
  const uniqueDivisions = [...new Set(staffList.map(s => s.division).filter(Boolean))];

  if (loading && staffList.length === 0) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="staff-page">
      {/* Modern Transparent Header */}
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FaUserTie className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">Data Staff</h1>
              <p className="header-subtitle">Kelola data staff dari NEO Feeder</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-glass btn-primary" onClick={() => setShowAddModal(true)}>
              <FiPlus /> 
              <span className="btn-text">Tambah Staff</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#4f46e5' }}>
            <FiFilter />
          </div>
          <div className="stat-details">
            <h3>{stats.total || 0}</h3>
            <p>Total Staff</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#10b981' }}>
            <FiRefreshCw />
          </div>
          <div className="stat-details">
            <h3>{stats.active || 0}</h3>
            <p>Staff Aktif</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#ef4444' }}>
            <FiRefreshCw />
          </div>
          <div className="stat-details">
            <h3>{stats.inactive || 0}</h3>
            <p>Staff Tidak Aktif</p>
          </div>
        </div>

        {/* Staff per Division */}
        {stats.byDivision && stats.byDivision.length > 0 && stats.byDivision
          .filter(d => d._id)
          .slice(0, 6)
          .map((div, idx) => {
            const colors = ['#8b5cf6', '#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#6366f1'];
            return (
              <div key={idx} className="program-stat-card">
                <div className="program-stat-content">
                  <div className="program-icon" style={{ background: colors[idx % colors.length] }}>
                    <FaUserTie />
                  </div>
                  <div className="program-info">
                    <div className="program-count">{div.count}</div>
                    <div className="program-label">Staff</div>
                  </div>
                </div>
                <div className="program-name" title={div._id}>
                  {div._id}
                </div>
              </div>
            );
          })}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="filters">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Cari nama, NIP..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Semua Status</option>
            <option value="Aktif">Aktif</option>
            <option value="Tidak Aktif">Tidak Aktif</option>
            <option value="Cuti">Cuti</option>
          </select>

          <select
            className="filter-select"
            value={divisionFilter}
            onChange={(e) => setDivisionFilter(e.target.value)}
          >
            <option value="">Semua Divisi</option>
            {uniqueDivisions.map((div, idx) => (
              <option key={idx} value={div}>{div}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Staff Table */}
      <div className="card">
        <h3>Daftar Staff</h3>
        {staffList.length === 0 ? (
          <div className="empty-state">
            <p>
              {searchTerm || statusFilter || divisionFilter
                ? 'Tidak ada staff yang sesuai dengan filter'
                : 'Belum ada data staff. Silakan tambah staff atau lakukan sinkronisasi dari NEO Feeder.'}
            </p>
          </div>
        ) : (
          <div className="modern-table-container">
            <table className="modern-table accent-purple">
              <thead>
                <tr>
                  <th>Foto</th>
                  <th>NIP</th>
                  <th>Nama Lengkap</th>
                  <th>Divisi</th>
                  <th>Posisi</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {staffList.map((staff) => (
                  <tr key={staff._id}>
                    <td>
                      <div className="staff-avatar-table">
                        <img 
                          src={staff.photo && staff.photo !== 'default-avatar.png' 
                            ? `/uploads/staff/${staff.photo}` 
                            : '/default-avatar.png'} 
                          alt={staff.name} 
                        />
                      </div>
                    </td>
                    <td>{staff.nip || '-'}</td>
                    <td>
                      <strong>{staff.name}</strong>
                    </td>
                    <td>{staff.division || '-'}</td>
                    <td>{staff.position || '-'}</td>
                    <td>{getStatusBadge(staff.isActive, staff.status)}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="table-btn table-btn-view"
                          onClick={() => handleViewDetail(staff)}
                          title="Lihat Detail"
                        >
                          <FiEye />
                        </button>
                        <button
                          className="table-btn table-btn-delete"
                          onClick={() => handleDeleteStaff(staff._id)}
                          title="Hapus"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Detail */}
      {showModal && selectedStaff && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {!isEditMode && (
                  <div className="staff-avatar-modal">
                    <img src={photoPreview || '/default-avatar.png'} alt="Avatar" />
                  </div>
                )}
                <div>
                  <h3>Detail Biodata Staff</h3>
                  {!isEditMode && selectedStaff && (
                    <p style={{ margin: '5px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                      {selectedStaff.name} - {selectedStaff.nip || 'Tanpa NIP'}
                    </p>
                  )}
                </div>
              </div>
              <div className="modal-header-actions">
                {!isEditMode ? (
                  <button className="btn-edit-modal" onClick={handleEditMode}>
                    <FiEdit2 /> Edit
                  </button>
                ) : (
                  <>
                    <button className="btn-save-modal" onClick={handleSaveEdit}>
                      <FiSave /> Simpan
                    </button>
                    <button className="btn-cancel-modal" onClick={handleCancelEdit}>
                      <FiX /> Batal
                    </button>
                  </>
                )}
                <button className="modal-close" onClick={handleCloseModal}>
                  <FiX />
                </button>
              </div>
            </div>
            <div className="modal-body">
              {/* Photo Section */}
              {isEditMode && (
                <div className="detail-section photo-section-staff">
                  <h4 className="section-title">Foto Profil</h4>
                  <div className="photo-upload-container-staff">
                    <div className="photo-preview-staff">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Preview" />
                      ) : (
                        <div className="photo-placeholder-staff">
                          <FiSave style={{ fontSize: '48px', opacity: 0.3 }} />
                          <p>Belum ada foto</p>
                        </div>
                      )}
                    </div>
                    <div className="photo-actions-staff">
                      <label htmlFor="staff-photo-input" className="btn btn-outline">
                        Pilih Foto
                      </label>
                      <input
                        id="staff-photo-input"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        style={{ display: 'none' }}
                      />
                      {photoPreview && photoPreview !== '/default-avatar.png' && (
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={handleRemovePhoto}
                        >
                          <FiX /> Hapus
                        </button>
                      )}
                      <small className="text-muted">
                        Format: JPG, PNG, GIF (Max 5MB)
                      </small>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Biodata Dasar */}
              <div className="detail-section">
                <h4 className="section-title">Biodata Dasar</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>NIP</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.nip || ''}
                        onChange={(e) => handleInputChange('nip', e.target.value)}
                      />
                    ) : (
                      <p>{selectedStaff.nip || '-'}</p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Nama Lengkap</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                      />
                    ) : (
                      <p><strong>{selectedStaff.name}</strong></p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Jenis Kelamin</label>
                    {isEditMode ? (
                      <select
                        className="edit-select"
                        value={editForm.gender || ''}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                      >
                        <option value="">Pilih</option>
                        <option value="L">Laki-laki</option>
                        <option value="P">Perempuan</option>
                      </select>
                    ) : (
                      <p>{selectedStaff.gender === 'L' ? 'Laki-laki' : selectedStaff.gender === 'P' ? 'Perempuan' : '-'}</p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Tempat Lahir</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.birthPlace || ''}
                        onChange={(e) => handleInputChange('birthPlace', e.target.value)}
                      />
                    ) : (
                      <p>{selectedStaff.birthPlace || '-'}</p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Tanggal Lahir</label>
                    {isEditMode ? (
                      <input
                        type="date"
                        className="edit-input"
                        value={editForm.birthDate || ''}
                        onChange={(e) => handleInputChange('birthDate', e.target.value)}
                      />
                    ) : (
                      <p>{formatDate(selectedStaff.birthDate)}</p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Email</label>
                    {isEditMode ? (
                      <input
                        type="email"
                        className="edit-input"
                        value={editForm.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                      />
                    ) : (
                      <p>{selectedStaff.email || '-'}</p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Telepon</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                      />
                    ) : (
                      <p>{selectedStaff.phone || '-'}</p>
                    )}
                  </div>
                  <div className="detail-item full-width">
                    <label>Alamat</label>
                    {isEditMode ? (
                      <textarea
                        className="edit-input"
                        value={editForm.address || ''}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        rows="3"
                      />
                    ) : (
                      <p>{selectedStaff.address || '-'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Informasi Kepegawaian */}
              <div className="detail-section">
                <h4 className="section-title">Informasi Kepegawaian</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Divisi</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.division || ''}
                        onChange={(e) => handleInputChange('division', e.target.value)}
                      />
                    ) : (
                      <p>{selectedStaff.division || '-'}</p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Posisi/Jabatan</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.position || ''}
                        onChange={(e) => handleInputChange('position', e.target.value)}
                      />
                    ) : (
                      <p>{selectedStaff.position || '-'}</p>
                    )}
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    {isEditMode ? (
                      <select
                        className="edit-select"
                        value={editForm.isActive ? 'Aktif' : editForm.status || 'Tidak Aktif'}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleInputChange('status', value);
                          handleInputChange('isActive', value === 'Aktif');
                        }}
                      >
                        <option value="Aktif">Aktif</option>
                        <option value="Tidak Aktif">Tidak Aktif</option>
                        <option value="Cuti">Cuti</option>
                      </select>
                    ) : (
                      <p>{getStatusBadge(selectedStaff.isActive, selectedStaff.status)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Add Staff */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); setAddMode(''); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{addMode ? (addMode === 'manual' ? 'Tambah Staff Manual' : 'Import Staff dari Excel') : 'Pilih Metode Tambah Staff'}</h3>
              <button className="modal-close" onClick={() => { setShowAddModal(false); setAddMode(''); }}>
                <FiX />
              </button>
            </div>
            
            {!addMode ? (
              /* Selection Mode */
              <div className="modal-body">
                <div className="add-mode-selection">
                  <div className="mode-card" onClick={() => setAddMode('manual')}>
                    <div className="mode-icon">
                      <FiEdit2 />
                    </div>
                    <h4>Input Manual</h4>
                    <p>Tambahkan data staff satu per satu melalui form</p>
                  </div>
                  <div className="mode-card" onClick={() => setAddMode('excel')}>
                    <div className="mode-icon">
                      <FiDownload />
                    </div>
                    <h4>Import Excel</h4>
                    <p>Upload file Excel untuk menambahkan banyak staff sekaligus</p>
                  </div>
                </div>
              </div>
            ) : addMode === 'manual' ? (
              /* Manual Input Mode */
              <>
                <div className="modal-body">
                  <div className="detail-section">
                    <h4 className="section-title">Biodata Dasar</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>NIP *</label>
                        <input
                          type="text"
                          className="edit-input"
                          value={newStaffForm.nip}
                          onChange={(e) => handleNewStaffInputChange('nip', e.target.value)}
                          required
                        />
                      </div>
                      <div className="detail-item">
                        <label>Nama Lengkap *</label>
                        <input
                          type="text"
                          className="edit-input"
                          value={newStaffForm.name}
                          onChange={(e) => handleNewStaffInputChange('name', e.target.value)}
                          required
                        />
                      </div>
                      <div className="detail-item">
                        <label>Jenis Kelamin</label>
                        <select
                          className="edit-select"
                          value={newStaffForm.gender}
                          onChange={(e) => handleNewStaffInputChange('gender', e.target.value)}
                        >
                          <option value="">Pilih</option>
                          <option value="L">Laki-laki</option>
                          <option value="P">Perempuan</option>
                        </select>
                      </div>
                      <div className="detail-item">
                        <label>Tempat Lahir</label>
                        <input
                          type="text"
                          className="edit-input"
                          value={newStaffForm.birthPlace}
                          onChange={(e) => handleNewStaffInputChange('birthPlace', e.target.value)}
                        />
                      </div>
                      <div className="detail-item">
                        <label>Tanggal Lahir</label>
                        <input
                          type="date"
                          className="edit-input"
                          value={newStaffForm.birthDate}
                          onChange={(e) => handleNewStaffInputChange('birthDate', e.target.value)}
                        />
                      </div>
                      <div className="detail-item">
                        <label>Email</label>
                        <input
                          type="email"
                          className="edit-input"
                          value={newStaffForm.email}
                          onChange={(e) => handleNewStaffInputChange('email', e.target.value)}
                        />
                      </div>
                      <div className="detail-item">
                        <label>Telepon</label>
                        <input
                          type="text"
                          className="edit-input"
                          value={newStaffForm.phone}
                          onChange={(e) => handleNewStaffInputChange('phone', e.target.value)}
                        />
                      </div>
                      <div className="detail-item full-width">
                        <label>Alamat</label>
                        <textarea
                          className="edit-input"
                          value={newStaffForm.address}
                          onChange={(e) => handleNewStaffInputChange('address', e.target.value)}
                          rows="3"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4 className="section-title">Informasi Kepegawaian</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Divisi</label>
                        <input
                          type="text"
                          className="edit-input"
                          value={newStaffForm.division}
                          onChange={(e) => handleNewStaffInputChange('division', e.target.value)}
                        />
                      </div>
                      <div className="detail-item">
                        <label>Posisi/Jabatan</label>
                        <input
                          type="text"
                          className="edit-input"
                          value={newStaffForm.position}
                          onChange={(e) => handleNewStaffInputChange('position', e.target.value)}
                        />
                      </div>
                      <div className="detail-item">
                        <label>Status</label>
                        <select
                          className="edit-select"
                          value={newStaffForm.status}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleNewStaffInputChange('status', value);
                            handleNewStaffInputChange('isActive', value === 'Aktif');
                          }}
                        >
                          <option value="Aktif">Aktif</option>
                          <option value="Tidak Aktif">Tidak Aktif</option>
                          <option value="Cuti">Cuti</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setAddMode('')}
                  >
                    <FiX /> Kembali
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={handleAddStaff}
                  >
                    <FiPlus /> Tambah Staff
                  </button>
                </div>
              </>
            ) : (
              /* Excel Import Mode */
              <>
                <div className="modal-body">
                  <div className="excel-upload-section">
                    <div className="upload-info">
                      <p><strong>Format Excel yang dibutuhkan:</strong></p>
                      <ul>
                        <li>Kolom: NIP, Nama, Email, Telepon, Jenis Kelamin, Tempat Lahir, Tanggal Lahir, Alamat, Divisi, Posisi, Status</li>
                        <li>Format file: .xls atau .xlsx</li>
                        <li>Baris pertama adalah header</li>
                      </ul>
                    </div>
                    
                    <div className="file-upload-area">
                      <input
                        type="file"
                        id="excel-file"
                        accept=".xls,.xlsx"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="excel-file" className="file-upload-label">
                        <FiDownload style={{ fontSize: '48px', marginBottom: '10px' }} />
                        <p>{excelFile ? excelFile.name : 'Klik untuk pilih file Excel'}</p>
                        {excelFile && (
                          <small style={{ color: '#10b981' }}>✓ File siap diupload</small>
                        )}
                      </label>
                    </div>

                    <div className="download-template">
                      <p>Belum punya template?</p>
                      <a href="/templates/template-staff.xlsx" download className="btn btn-outline">
                        <FiDownload /> Download Template Excel
                      </a>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => { setAddMode(''); setExcelFile(null); }}
                  >
                    <FiX /> Kembali
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={handleExcelUpload}
                    disabled={!excelFile}
                  >
                    <FiDownload /> Upload Excel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Sync Config */}
      {showSyncModal && (
        <div className="modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="modal-content modal-sync" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Sinkronisasi Data Staff</h3>
              <button className="modal-close" onClick={() => setShowSyncModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="sync-info">
                <p>Mengambil data staff dari Neo Feeder</p>
              </div>

              <div className="form-group">
                <label>Mode Sinkronisasi</label>
                <select
                  className="form-control"
                  value={syncConfig.syncMode}
                  onChange={(e) => setSyncConfig({ ...syncConfig, syncMode: e.target.value })}
                >
                  <option value="upsert">Upsert (Insert + Update)</option>
                  <option value="insert">Insert Only (Tambah Baru)</option>
                  <option value="update">Update Only (Update Existing)</option>
                </select>
                <small className="form-text">
                  {syncConfig.syncMode === 'upsert' && '✓ Tambah data baru atau update jika sudah ada'}
                  {syncConfig.syncMode === 'insert' && '✓ Hanya tambah data baru, skip yang sudah ada'}
                  {syncConfig.syncMode === 'update' && '✓ Hanya update data yang sudah ada, skip yang baru'}
                </small>
              </div>

              <div className="form-group">
                <label>Jumlah Data (Limit)</label>
                <input
                  type="number"
                  className="form-control"
                  value={syncConfig.limit}
                  onChange={(e) => setSyncConfig({ ...syncConfig, limit: parseInt(e.target.value) || 50 })}
                  min="1"
                  max="1000"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowSyncModal(false)}
              >
                Batal
              </button>
              <button
                className="btn btn-success"
                onClick={handleSyncStaff}
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <div className="spinner-small"></div>
                    Sinkronisasi...
                  </>
                ) : (
                  <>
                    <FiDownload /> Mulai Sinkronisasi
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Staff;
